import { navigate, PageProps } from 'gatsby';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flag, Icon, MenuButton } from '@opswat/ui';
import { useGTM } from 'hooks/useGTM';
import { PageHeader } from 'components/layout/PageHeader';
import { Footer } from 'components/footer/Footer';
import { useUser } from 'services/User';
import { canAccessHomePage, canAccessPage } from 'services/accessControl';
import { canAccessStatsPage } from 'services/statsAccessGranting';
import { Logo } from 'components/layout/Logo';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { Theme, useTheme } from 'hooks/useTheme';
import { useLanguage } from 'hooks/useLanguage';
import { DashboardTabs, IntegrationsTabs } from 'services/tabs';
import { useGlobalDragDrop } from 'hooks/useGlobalDragDrop';
import { freeScanOptions } from 'hooks/useFileScanFlow';

type PublicLayoutProps = Omit<PageProps, 'children'> & { children?: ReactNode; };

export const PublicLayout = ({ children, location }: PublicLayoutProps) => {
    const pathname = location?.pathname;
    const { user } = useUser();
    const { t } = useTranslation();
    const { trackClick } = useGTM();
    const { currentTheme, setTheme } = useTheme();
    const { languages, changeLanguage } = useLanguage();

    const isHomePage = pathname === '/';
    const { isDragOver, isUploading } = useGlobalDragDrop({
        enabled: isHomePage,
        fallbackOptions: freeScanOptions,
    });

    const isNavActive = (basePath: string) =>
        Boolean(pathname) && (pathname === basePath || pathname.startsWith(`${basePath}/`));

    const navButtons = [
        {
            id: 'layout-public-nav-product',
            text: t('Product'),
            basePath: '/product',
            onClick: () => {
                trackClick('layout-public-nav-product');
                navigate('/product');
            },
        },
        {
            id: 'layout-public-nav-integrations',
            text: t('Integrations'),
            basePath: '/integrations',
            onClick: () => {
                trackClick('layout-public-nav-integrations');
                navigate(`/integrations/${IntegrationsTabs.All}`);
            },
        },
        ...(canAccessStatsPage(user)
            ? [
                {
                    id: 'layout-public-nav-explore-data',
                    text: t('Explore Data'),
                    basePath: '/explore-data',
                    onClick: () => {
                        trackClick('layout-public-nav-explore-data');
                        navigate('/explore-data');
                    },
                },
            ]
            : []),
        {
            id: 'layout-public-nav-blog',
            text: t('Blog'),
            basePath: '/blog',
            onClick: () => {
                trackClick('layout-public-nav-blog');
                navigate('/blog');
            },
        },
        {
            id: 'layout-public-nav-cve-vulnerabilities',
            text: t('CVE Vulnerabilities'),
            basePath: '/cve-vulnerabilities',
            onClick: () => {
                trackClick('layout-public-nav-cve-vulnerabilities');
                navigate('/cve-vulnerabilities');
            },
        },
        {
            id: 'layout-public-nav-licensing',
            text: t('Licensing'),
            basePath: '/licensing',
            onClick: () => {
                trackClick('layout-public-nav-licensing');
                navigate('/licensing');
            },
        },
        ...(canAccessPage(user, 'store')
            ? [
                {
                    id: 'layout-public-nav-store',
                    text: t('Store'),
                    basePath: '/store',
                    onClick: () => {
                        trackClick('layout-public-nav-store');
                        navigate('/store');
                    },
                },
            ]
            : []),
    ];

    return (
        <div
            className={`tw:font-roboto tw:w-screen tw:h-screen tw:flex tw:flex-col ${isDragOver || isUploading ? 'tw:opacity-50' : ''
                }`}
        >
            <header
                className='tw:relative tw:flex tw:flex-row tw:justify-between tw:items-center tw:w-full tw:h-60 tw:px-20 tw:border-b tw:border-neutrals-200 tw:dark:border-neutrals-1000'
                role='banner'
            >
                <Logo />
                <div className='tw:flex tw:flex-row tw:items-center tw:self-stretch tw:gap-20'>
                    <div className='cm-public-nav-inline tw:flex tw:flex-row tw:items-center tw:self-stretch tw:gap-20'>
                        {navButtons.map(({ basePath, ...props }) => {
                            const active = isNavActive(basePath);
                            return (
                                <div key={basePath} className='tw:relative tw:flex tw:items-center tw:self-stretch'>
                                    <Button
                                        variant='tertiary'
                                        aria-current={active ? 'page' : undefined}
                                        className={
                                            active
                                                ? 'tw:text-blue-700 tw:dark:text-blue-400'
                                                : 'tw:text-neutrals-800 tw:dark:text-neutrals-500 tw:hover:text-neutrals-1200 tw:dark:hover:text-white'
                                        }
                                        {...props}
                                    />
                                    {active && (
                                        <span
                                            aria-hidden='true'
                                            className='tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-2 tw:bg-blue-700 tw:dark:bg-blue-400'
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <span className='cm-public-nav-menu'>
                        <MenuButton>
                            <MenuButton.Trigger
                                hasArrow={false}
                                aria-label={t('Toggle navigation')}
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='menu' />
                            </MenuButton.Trigger>
                            <MenuButton.Dropdown className='tw:w-220'>
                                {navButtons.map(({ basePath, onClick, ...rest }) => {
                                    const active = isNavActive(basePath);
                                    return (
                                        <MenuButton.Item key={basePath} onClick={onClick}>
                                            <span
                                                className={
                                                    active ? 'tw:text-blue-700 tw:dark:text-blue-400' : undefined
                                                }
                                            >
                                                {rest.text}
                                            </span>
                                        </MenuButton.Item>
                                    );
                                })}
                                <MenuButton.Separator />
                                <MenuButton.Item
                                    onClick={() =>
                                        setTheme(currentTheme.value === Theme.Dark ? Theme.Light : Theme.Dark)
                                    }
                                >
                                    <div className='tw:flex tw:items-center tw:gap-8'>
                                        <Icon iconName={currentTheme.icon} />
                                        <span>{currentTheme.label}</span>
                                    </div>
                                </MenuButton.Item>
                                {languages.map(({ code, name, flag, isSelected }) => (
                                    <MenuButton.Item key={code} onClick={() => changeLanguage(code)}>
                                        <div className='tw:flex tw:items-center tw:gap-8'>
                                            <Flag countryCode={flag ?? 'US'} />
                                            <span className='tw:flex-1'>{name}</span>
                                            {isSelected && <Icon iconName='check' />}
                                        </div>
                                    </MenuButton.Item>
                                ))}
                            </MenuButton.Dropdown>
                        </MenuButton>
                    </span>
                    {user?.isLoggedIn && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => navigate(`/dashboard/${DashboardTabs.Security}`)}
                                data-testid='public-layout-dashboard-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='dashboard' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    {pathname !== '/' && user?.isLoggedIn && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => {
                                    if (canAccessHomePage(user)) {
                                        navigate('/');
                                    } else {
                                        navigate(`/dashboard/${DashboardTabs.Security}`);
                                    }
                                }}
                                data-testid='public-layout-home-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='home' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    <PageHeader hideControlsOnMobile />
                </div>
            </header>
            <main className='tw:w-full tw:flex tw:flex-col tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden'>
                <div className='tw:flex-1'>{children}</div>
                {!pathname?.startsWith('/results') && <Footer />}
            </main>
            {isUploading && (
                <div className='tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center'>
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};
